import { SortingState } from "@tanstack/react-table";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row, Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import {
  fetchrecordLevelInstock,
  deleteReorderLevel,
  exportReorderLevelCSV,
} from "slices/Inventory/ReorderLevel/InStock/thunk";
import { CiEdit } from "react-icons/ci";
import { FaRegTrashCan } from "react-icons/fa6";
import { toast } from "react-toastify";
import { DeleteModal } from "Common/DeleteModal";

import { Visibility } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { exportPendingPOCSV } from "slices/Warehouse/ItemInward/PendingPO/thunk";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";

import { IoMdAdd } from "react-icons/io";
import { MecordColors } from "Constants/Color";
import ReorderLevelForm from "../../ReorderLevelForm/ReorderLevelForm";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { showConfirm } from "helpers/swalHelper";
import StatusChip from "pages/Enquiry/Components/StatusChip";

// type GRNProps = {
//   warehouseId: string;
// };

const ReorderLevel: React.FC = () => {
  document.title = "Reorder Level";

  const tableState = usePreservedTableState({
    key: TableKeys.INVENTORY.REORDER_LEVEL,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [data, setData] = useState([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const [selectedReorderLevel, setSelectedReorderLevel] = useState<any>(null);
  const [showReorderLevelModal, setShowReorderLevelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const Warehouse = createSelector(
    (state: any) => state.recordLevelIteamWiseReducer,
    (item) => ({
      list: Array.isArray(item?.recordLevelInStockList)
        ? item.recordLevelInStockList
        : [],
      rowCount: item?.rowCount || 0,
      loading: item.loading,
      error: item.error,
    }),
  );

  const { list, rowCount, loading, error } = useSelector(Warehouse);

  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.inventory?.app_name,
    app_modules?.inventory?.modules?.reorder_level,
  );

  const fetchData = useCallback(() => {
    dispatch(
      fetchrecordLevelInstock({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortDirection,
        search,
        filters: filterValue,
      }),
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
  ]);

  const handleDelete = async (reorderLevelId: string) => {
    console.log("handleDelete called with reorderLevelId:", reorderLevelId);
    if (reorderLevelId) {
      console.log("Deleting Reorder Level with ID:", reorderLevelId);
      try {
        await dispatch(
          deleteReorderLevel(reorderLevelId),
        ).unwrap();
        toast.success("Reorder Level deleted successfully");
        fetchData();
      } catch (error: any) {
        toast.error(error?.message || "Failed to delete");
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (list && list.length > 0) {
      setData(list);
      console.log("list", list);
    } else {
      setData([]);
    }
  }, [list]);

  // Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  const columns = useMemo(
    () => [
      // {
      //   header: "Sr No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row, table }: { row: any; table: any }) => {
      //     const pageIndex = table.getState().pagination.pageIndex;
      //     const pageSize = table.getState().pagination.pageSize;
      //     const rowIndex = row.index;
      //     return pageIndex * pageSize + rowIndex + 1;
      //   },
      // },
      {
        header: "Products",
        accessorKey: "product.product_name",
        id: "item_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.INVENTORY?.REORDER_LEVEL?.FILTER_DROPDOWN(
          "item_name",
        )}`,
        cell: ({ row }: { row: any }) =>
          row.original.product.product_name || "-",
      },
      {
        header: "Product SKU",
        accessorKey: "product.product_sku",
        id: "item_sku",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.INVENTORY?.REORDER_LEVEL?.FILTER_DROPDOWN(
          "item_sku",
        )}`,
        cell: ({ row }: { row: any }) =>
          row.original.product?.product_sku || "-",
      },
      {
        header: "Category",
        accessorKey: "product.category",
        id: "category",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.INVENTORY?.REORDER_LEVEL?.FILTER_DROPDOWN(
          "category",
        )}`,
        cell: ({ row }: { row: any }) => row.original.product?.category || "-",
      },
      {
        header: "Sub Category",
        accessorKey: "product.sub_category",
        id: "sub_category",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.INVENTORY?.REORDER_LEVEL?.FILTER_DROPDOWN(
          "sub_category",
        )}`,
        cell: ({ row }: { row: any }) =>
          row.original.product?.sub_category || "-",
      },
      {
        header: "Make",
        accessorKey: "product.make",
        id: "make",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.INVENTORY?.REORDER_LEVEL?.FILTER_DROPDOWN(
          "make",
        )}`,
        cell: ({ row }: { row: any }) => row.original.product?.make || "-",
      },
      {
        header: "Model",
        accessorKey: "product.model",
        id: "model",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.INVENTORY?.REORDER_LEVEL?.FILTER_DROPDOWN(
          "model",
        )}`,
        cell: ({ row }: { row: any }) => row.original.product?.model || "-",
      },
      {
        header: "Unit",
        accessorKey: "product.unit",
        id: "unit",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.INVENTORY?.REORDER_LEVEL?.FILTER_DROPDOWN(
          "unit",
        )}`,
        cell: ({ row }: { row: any }) => row.original.product?.unit || "-",
      },
      {
        header: "Total Stock",
        accessorKey: "total_stock",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) =>
          row.original.product?.total_stock ?? "-",
      },
      {
        header: "Good Quantity",
        accessorKey: "robust_qty",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) =>
          row.original.product?.robust_qty ?? "-",
      },
      {
        header: "Re-Order Level",
        accessorKey: "reorder_level",
        id: "minimum_quantity",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.INVENTORY?.REORDER_LEVEL?.FILTER_DROPDOWN(
          "minimum_quantity",
        )}`,
        cell: ({ row }: { row: any }) => row.original.reorder_level ?? "-",
      },
      {
        header: "Re-Order Level Status",
        accessorKey: "re_order_level_status",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => {
          const totalStock = Number(row.original.product?.total_stock) || 0;
          const reorderLevel = Number(row.original.reorder_level) || 0;

          const status = totalStock < reorderLevel ? "Low Stock" : "In Stock";
          return <StatusChip status={status} />;

          const getStatusColor = (status: string) => {
            switch (status?.toLowerCase()) {
              case "low stock":
                return "#FFB2B2";
              case "in stock":
                return "#B7FAEB";
              default:
                return "#ccc";
            }
          };

          return (
            <StatusBadge
              status={status}
              bgColor={getStatusColor(status)}
              color="#000000ff"
            />
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user.username}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.updated_by_user.username}
            date={row.original.updated_at}
          />
        ),
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const data = row.original;
          const menuItems = [
            {
              icon: <EditIcon fontSize="small" color="primary" />,
              label: "Edit",
              permission: canUpdate,
              onClick: (item: any) => {
                setSelectedReorderLevel(data);
                setShowReorderLevelModal(true);
              },
              color: "primary",
            },
            {
              icon: <DeleteOutlineIcon fontSize="small" color="error" />,
              label: "Delete",
              permission: canUpdate,
              onClick: async (item: any) => {
                setSelectedReorderLevel(data);
                const result = await showConfirm(
                  "Are you sure you want to delete this Reorder Level?",
                  undefined,
                  "Yes, Delete",
                  "Cancel",
                );

                if (result.isConfirmed) {
                  await handleDelete(row.original.reorder_level_id); // ✅ reuse existing function
                }
              },
              color: "primary",
            },
          ];
          return <DynamicMenuItem row={data} menuItems={menuItems} />;
        },
      },
    ],
    [dispatch],
  );

  return (
    <div
      className="d-flex flex-column"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      <div className="flex-shrink-0">
        <div className="mecord-toolbar-title mb-2">
          <h6>Reorder Level</h6>
          {canCreate && (
            <Button
              onClick={() => {
                setSelectedReorderLevel(null);
                setShowReorderLevelModal(true);
              }}
              style={{
                backgroundColor: MecordColors.PrimaryButtonBGColor,
                border: "none",
                borderRadius: "8px",
                padding: "0 12px",
                height: 34,
                fontSize: "13px",
                fontWeight: "600",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <IoMdAdd size={18} /> Define Reorder Level
            </Button>
          )}
        </div>

        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
                excludedColumns={["action"]}
              />
            )}
            {canView && (
              <ExportButton
                onExport={async () => {
                  return await exportReorderLevelCSV({
                    search,
                    filters: filterValue,
                  });
                }}
              />
            )}
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="text-center py-4 text-danger">
            <h5>Error: {error}</h5>
          </div>
        ) : (
          <MasterTableContainer
            columns={columns}
            data={data || []}
            totalRecords={
              Number.isNaN(rowCount) || !rowCount ? 0 : Number(rowCount)
            }
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
            sorting={sorting}
            onSortingChange={setSorting}
            isBordered={false}
            isPagination={true}
            manualSorting={true}
            onTableReady={setTableInstance}
          />
        )}
      </div>

      <Modal
        show={showReorderLevelModal}
        onHide={() => setShowReorderLevelModal(false)}
        centered
        size="lg"
      >
        <Modal.Header className="border-bottom border-1 pb-2" closeButton>
          <Modal.Title style={{ fontSize: "20px" }}>
            {selectedReorderLevel
              ? "Edit Reorder Level"
              : "Define Reorder Level"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <ReorderLevelForm
            initialData={selectedReorderLevel}
            onClose={() => setShowReorderLevelModal(false)}
            onSuccess={fetchData}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ReorderLevel;
